import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MetaTags from 'components/common/MetaTags';
import PageHeader from 'components/NewDesign/PageHeader';
import MainContent from 'components/NewDesign/MainContent';
import AthleteSeasonPoints from 'components/admin/reports/AthleteSeasonPoints';
import {
  exportAthleteSeasonPointsToFile,
  exportAthleteSeasonPointsMeetBreakdownToFile,
  recalculateAthleteSeasonPoints
} from 'api/reportsApi';
import useActiveSeasonName from 'hooks/useActiveSeasonName';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { exportAndDownloadFile } from 'utils/fileDownload';
import routes from 'routes';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import { organisationWordOf } from 'utils/clubFeatures';
import { getErrorMessage } from 'utils/api';

const AthleteSeasonPointsPage = () => {
  const navigate = useNavigate();
  const { selectedClub } = useSelector((state) => state.user || {});
  const [reportData, setReportData] = useState(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculationTrigger, setRecalculationTrigger] = useState(0);
  // The report (and recalculation) is active-season-only, so the active season
  // name is the right subtitle.
  const seasonName = useActiveSeasonName(selectedClub?.clubID);

  const handleRecalculatePoints = async () => {
    if (!selectedClub?.clubID) {
      toast.error('No club selected');
      return;
    }

    setIsRecalculating(true);
    
    // Show immediate feedback to user
    toast.info('Recalculating athlete points. This may take a moment...', {
      autoClose: false,
      toastId: 'recalculating'
    });
    
    try {
      const response = await recalculateAthleteSeasonPoints(selectedClub.clubID);
      
      // Dismiss the processing toast
      toast.dismiss('recalculating');
      
      if (response.data) {
        const { athleteCount, seasonID, pointsChanged } = response.data;
        // The API reports whether any athlete's points changed. An API that predates the field
        // sends nothing, and the message then stays neutral.
        const outcome =
          pointsChanged === true
            ? 'Totals were updated.'
            : pointsChanged === false
              ? 'Everything was already up to date, so nothing changed.'
              : '';
        toast.success(
          `Points recalculated for ${seasonID}. ${athleteCount} athletes processed. ${outcome}`.trim(),
          { autoClose: 6000 }
        );
        // Trigger data refresh
        setRecalculationTrigger(prev => prev + 1);
      }
    } catch (err) {
      // Dismiss the processing toast
      toast.dismiss('recalculating');
      console.error('[AthletePoints] Recalculation error: ', err);
      // A replay of the season already running (the hourly one, or another press) is not started again: the
      // server says so (409), and nothing has failed (Doug, 2026-10-27).
      if (err?.response?.status === 409) {
        toast.info(getErrorMessage(err), { autoClose: 6000 });
      } else {
        toast.error('Failed to recalculate athlete points');
      }
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleExportToFile = async () => {
    await exportAndDownloadFile(
      exportAthleteSeasonPointsToFile,
      [selectedClub.clubID, reportData?.sortBy],
      '',
      'The athlete season points report was successfully exported',
      'Fetching athlete season points data from the server...'
    );
  };

  const handleExportMeetBreakdown = async () => {
    await exportAndDownloadFile(
      exportAthleteSeasonPointsMeetBreakdownToFile,
      [selectedClub.clubID, reportData?.sortBy],
      '',
      'The meet breakdown was successfully exported',
      'Fetching meet breakdown data from the server...'
    );
  };

  const canOpenClubSettings =
    getUserPermissionLevel() >= UserPermissionLevel.Admin;

  const handlePointsSettings = () => {
    navigate(routes.admin.clubSettings.general + '#achievement-points');
  };

  const isExportButtonDisabled = !reportData || !reportData.hasData;

  return (
    <>
      <MetaTags title="SplitFast | Athlete Season Points Report" />
      <MainContent>
        <PageHeader title="Athlete Season Points" subTitle={seasonName} primarySubTitle>
          {/* Club Settings is for Admins and Owners only, so the button that
              opens it is too. */}
          {canOpenClubSettings && (
            <CustomNewButton
              variant="info"
              onClick={handlePointsSettings}
              label="Points Settings"
              tooltip={`Open the ${organisationWordOf(selectedClub)} settings page to view or edit the default achievement points.`}
            />
          )}
          <CustomNewButton
            variant="primary"
            onClick={handleRecalculatePoints}
            label="Recalculate Points"
            disabled={isRecalculating || !selectedClub}
            tooltip="Recalculate all athlete points for the active season from the latest results."
          />
          <CustomNewButton
            variant="info"
            onClick={handleExportToFile}
            label="Export to file"
            disabled={isExportButtonDisabled}
            tooltip="Download the season points report as a CSV file."
          />
          <CustomNewButton
            variant="info"
            onClick={handleExportMeetBreakdown}
            label="Export Meet Breakdown"
            disabled={isExportButtonDisabled}
            tooltip="Download a CSV showing each athlete's points at every meet in the season."
          />
        </PageHeader>
        {!selectedClub ? (
          <div style={{ minHeight: '100vh' }}/>
        ) : (
          <AthleteSeasonPoints
            clubID={selectedClub?.clubID}
            onDataChange={setReportData}
            recalculationTrigger={recalculationTrigger}
          />
        )}
      </MainContent>
    </>
  );
};

export default AthleteSeasonPointsPage;